interface AnalyticsCacheData {
  orderTrends: any;
  revenueGrowth: any;
  revenueSummary: {
    yesterday: number;
    last7days: number;
    last30days: number;
    last1year: number;
  };
  lastUpdated: string;
  userId: number;
}

const CACHE_KEY = 'analytics_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const analyticsCache = {
  // Check if cache exists and is valid
  isValid(): boolean {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return false;

      const data: AnalyticsCacheData = JSON.parse(cached);
      const now = new Date().getTime();
      const lastUpdated = new Date(data.lastUpdated).getTime();
      
      // Check if cache is less than 24 hours old
      return (now - lastUpdated) < CACHE_DURATION;
    } catch (error) {
      console.error('Error checking cache validity:', error);
      return false;
    }
  },

  // Get cached data
  get(): AnalyticsCacheData | null {
    try {
      if (!this.isValid()) return null;
      
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error getting cached data:', error);
      return null;
    }
  },

  // Set cache data
  set(data: Omit<AnalyticsCacheData, 'lastUpdated' | 'userId'>): void {
    try {
      const cacheData: AnalyticsCacheData = {
        ...data,
        lastUpdated: new Date().toISOString(),
        userId: this.getCurrentUserId(),
      };
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error setting cache data:', error);
    }
  },

  // Clear cache (called on login)
  clear(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  },

  // Get current user ID from auth token
  getCurrentUserId(): number {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return 0;
      
      const decoded = JSON.parse(atob(token));
      return decoded.uid || 0;
    } catch (error) {
      console.error('Error getting user ID:', error);
      return 0;
    }
  },

  // Check if cache belongs to current user
  isForCurrentUser(): boolean {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return false;
      
      const data: AnalyticsCacheData = JSON.parse(cached);
      const currentUserId = this.getCurrentUserId();
      
      return data.userId === currentUserId;
    } catch (error) {
      console.error('Error checking user match:', error);
      return false;
    }
  }
};
